import React, { useContext, useState, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Button } from "primereact/button";
import { Password } from "primereact/password";
import { useNavigate, useParams } from "react-router-dom";
import { Context } from "../../../index";
import CustomLink from "../../Shared/CustomLink/CustomLink";
import Logo from "../../../assets/mainLogo.svg";
import Bacground from "../../../assets/backgroundLogin.jpg";

const MIN_PASSWORD_LENGTH = 8;

const RecoverPasswordForm = observer(() => {
    const { token } = useParams();
    const { store } = useContext(Context);
    const navigate = useNavigate();

    const [passwords, setPasswords] = useState({
        password: { value: "", error: "" },
        passwordRepeat: { value: "", error: "" }
    });
  
    const [infoView, setInfoView] = useState(false);
    const [info, setInfo] = useState("");

    const validatePassword = useCallback((value: string): string => {
        if (!value) {
            return "Введите пароль";
        }
        if (value.length < MIN_PASSWORD_LENGTH) {
            return `Минимальная длина пароля - ${MIN_PASSWORD_LENGTH} символов`;
        }
        return "";
    }, []);

    const validateRepeatPassword = useCallback((value: string, password: string): string => {
        if (!value) {
            return "Введите подтверждение пароля";
        }
        if (value !== password) {
            return "Пароли не совпадают";
        }
        return "";
    }, []);

    const handlePasswordChange = useCallback((field: keyof typeof passwords) => 
        (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPasswords(prev => ({
            ...prev,
            [field]: { value, error: "" }
        }));
    }, []);

    const handleBlur = useCallback((field: keyof typeof passwords) => () => {
        const error = field === 'password' 
        ? validatePassword(passwords.password.value)
        : validateRepeatPassword(passwords.passwordRepeat.value, passwords.password.value);
        
        setPasswords(prev => ({
            ...prev,
            [field]: { ...prev[field], error }
        }));
    }, [passwords.password.value, passwords.passwordRepeat.value, validatePassword, validateRepeatPassword]);

    const handleRecover = useCallback(async () => {
        const passwordError = validatePassword(passwords.password.value);
        const repeatError = validateRepeatPassword(passwords.passwordRepeat.value, passwords.password.value);

        if (passwordError || repeatError) {
            setPasswords(prev => ({
                password: { ...prev.password, error: passwordError },
                passwordRepeat: { ...prev.passwordRepeat, error: repeatError }
            }));
            return;
        }

        try {
            const error = await store.recoverPassword({
                password: passwords.password.value,
                token: token!,
            });
            
            setInfoView(true);
            setInfo(error ? error.message : "Пароль успешно изменен");
        } catch (err) {
            setInfoView(true);
            setInfo("Произошла ошибка при восстановлении пароля");
        }
    }, [passwords.password.value, passwords.passwordRepeat.value, token, store]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleRecover();
        }
    }, [handleRecover]);

    const formFields = useMemo(() => [
        {
            id: "login_pass",
            label: "Новый пароль*",
            value: passwords.password.value,
            error: passwords.password.error,
            onChange: handlePasswordChange('password'),
            onBlur: handleBlur('password'),
            placeholder: "********"
        },
        {
            id: "login_pass_repeat",
            label: "Подтверждение пароля*",
            value: passwords.passwordRepeat.value,
            error: passwords.passwordRepeat.error,
            onChange: handlePasswordChange('passwordRepeat'),
            onBlur: handleBlur('passwordRepeat'),
            placeholder: "********"
        }
    ], [passwords, handlePasswordChange, handleBlur]);

    const infoContent = useMemo(() => (
        <>
        <div className="form_description">
            <p className="form_text">{info}</p>
        </div>
        <div className="bottom_buttons">
            <p className="p_blue p_14 pointer_custom">
            <CustomLink to="/login">Вход</CustomLink>
            </p>
        </div>
        </>
    ), [info]);

    const passwordFormContent = useMemo(() => (
        <div onKeyDown={handleKeyPress}>
        {formFields.map(field => (
            <div key={field.id} className={`input_form_login ${field.error ? "input_with_error" : ""}`}>
                <label className="input_label" htmlFor={field.id}>
                    {field.label}
                </label>
                <Password
                    id={field.id}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    feedback={false}
                    placeholder={field.placeholder}
                    toggleMask
                    className={field.error ? "p-invalid" : ""}
                />
                {field.error && <p className="error_message">{field.error}</p>}
            </div>
        ))}
        <div className="login_btn">
            <Button
                label="Восстановить"
                onClick={handleRecover}
                disabled={!passwords.password.value || !passwords.passwordRepeat.value}
            />
        </div>
        <div className="bottom_buttons form__margin-top">
            <p className="p_blue p_14 pointer_custom">
                <CustomLink to="/login">Вход</CustomLink>
            </p>
            </div>
        </div>
    ), [formFields, handleRecover, handleKeyPress, passwords]);

    return (
        <div className="form_main_center">
            <div className="form_container form_container_fullscreen form_container_center">
                <div className="form_inner">
                    <div className="form_logo">
                        <img src={Logo} alt="Логотип компании" />
                    </div>
                    <div>
                        <h2 className="form_header form_text">
                        Восстановление пароля
                        </h2>
                    </div>
                    {infoView ? infoContent : passwordFormContent}
                </div>
            </div>
            <img
                src={Bacground}
                alt="Фон компании"
                className="background_image_fullscreen"
            />
        </div>
    );
});

export default RecoverPasswordForm;